import { Button } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  companies,
  contacts,
  externalReferences,
  offices,
  type Company,
  type Contact,
  type ExternalReference,
  type Office,
} from "../../../api/addressBook.service";
import LoadingState from "../../../components/LoadingState";
import { PageHeader } from "../../../components/PageHeader";
import { ROUTES } from "../../../routes";
import styles from "../AddressBook.module.css";

// ============================================================================
// Types
// ============================================================================

type Step = "select" | "review" | "confirm" | "done";

type WizardState = {
  targetCompany: Company | null;
  effectiveDate: string;
};

type ReviewData = {
  sourceOffices: Office[];
  targetOffices: Office[];
  sourceContacts: Contact[];
  targetContacts: Contact[];
  sourceRefs: ExternalReference[];
  targetRefs: ExternalReference[];
};

type DiffRow = {
  lineNumber: number | null;
  content: string;
  type: "removed" | "added" | "unchanged" | "gutter";
};

// ============================================================================
// Diff computation
// ============================================================================

function computeDiff(
  source: Company,
  target: Company,
): { sourceRows: DiffRow[]; targetRows: DiffRow[] } {
  const fields: Array<[string, keyof Company]> = [
    ["name", "name"],
    ["originalName", "originalName"],
    ["globalName", "globalName"],
    ["businessType", "businessType"],
    ["industry", "industry"],
    ["website", "website"],
    ["slug", "slug"],
    ["crmEntityNumber", "crmEntityNumber"],
    ["notes", "notes"],
    ["firmRef", "firmRef"],
  ];

  const sourceRows: DiffRow[] = [];
  const targetRows: DiffRow[] = [];
  let sourceLineNum = 1;
  let targetLineNum = 1;

  for (const [label, key] of fields) {
    const sv = source[key];
    const tv = target[key];
    const sVal = sv != null && sv !== "" ? String(sv) : null;
    const tVal = tv != null && tv !== "" ? String(tv) : null;

    if (sVal === tVal) {
      if (sVal !== null) {
        sourceRows.push({
          lineNumber: sourceLineNum++,
          content: `${label}: ${sVal}`,
          type: "unchanged",
        });
        targetRows.push({
          lineNumber: targetLineNum++,
          content: `${label}: ${tVal}`,
          type: "unchanged",
        });
      }
    } else {
      if (sVal !== null) {
        sourceRows.push({
          lineNumber: sourceLineNum++,
          content: `${label}: ${sVal}`,
          type: "removed",
        });
        sourceRows.push({ lineNumber: null, content: "", type: "gutter" });
      }
      if (tVal !== null) {
        targetRows.push({ lineNumber: null, content: "", type: "gutter" });
        targetRows.push({
          lineNumber: targetLineNum++,
          content: `${label}: ${tVal}`,
          type: "added",
        });
      }
    }
  }

  return { sourceRows, targetRows };
}

// ============================================================================
// WizardStepper component
// ============================================================================

type StepperStep = { label: string; status: "completed" | "active" | "upcoming" };

function WizardStepper({ steps }: { steps: StepperStep[] }) {
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: "2rem" }}>
      {steps.map((s, i) => (
        <>
          {i > 0 && (
            <div
              style={{
                flex: 1,
                height: 2,
                backgroundColor:
                  s.status === "upcoming" ? "var(--color-border)" : "var(--color-primary, #1a56db)",
              }}
            />
          )}
          <div
            key={s.label}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 600,
                backgroundColor:
                  s.status === "completed"
                    ? "var(--color-primary, #1a56db)"
                    : s.status === "active"
                      ? "var(--color-primary, #1a56db)"
                      : "var(--color-background-secondary)",
                color: s.status === "upcoming" ? "var(--color-text-secondary)" : "white",
                border:
                  s.status === "upcoming"
                    ? "2px solid var(--color-border)"
                    : "2px solid var(--color-primary, #1a56db)",
              }}
            >
              {s.status === "completed" ? "✓" : i + 1}
            </div>
            <span
              style={{
                fontSize: 12,
                color:
                  s.status === "active"
                    ? "var(--color-text-primary)"
                    : "var(--color-text-secondary)",
                fontWeight: s.status === "active" ? 600 : 400,
                whiteSpace: "nowrap",
              }}
            >
              {s.label}
            </span>
          </div>
        </>
      ))}
    </div>
  );
}

function buildStepperSteps(step: Step): StepperStep[] {
  const labels = ["Select Target", "Review Comparison", "Confirm"];
  const activeIndex = step === "select" ? 0 : step === "review" ? 1 : step === "confirm" ? 2 : 3;

  return labels.map((label, i) => ({
    label,
    status: i < activeIndex ? "completed" : i === activeIndex ? "active" : "upcoming",
  }));
}

// ============================================================================
// DiffPanel component
// ============================================================================

function DiffPanel({
  title,
  rows,
  borderRight,
}: {
  title: string;
  rows: DiffRow[];
  borderRight?: boolean;
}) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        borderRight: borderRight ? "1px solid rgb(229,231,235)" : undefined,
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          backgroundColor: "rgb(249,250,251)",
          borderBottom: "1px solid rgb(229,231,235)",
          fontWeight: 600,
          fontSize: 14,
          color: "rgb(55,65,81)",
        }}
      >
        {title}
      </div>
      <div style={{ overflowY: "auto", maxHeight: 600, backgroundColor: "white" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {rows.map((row, i) => {
              const isRemoved = row.type === "removed";
              const isAdded = row.type === "added";
              const _isGutter = row.type === "gutter";
              const bg = isRemoved ? "rgb(254,226,226)" : isAdded ? "rgb(220,252,231)" : "white";
              const color = isRemoved
                ? "rgb(153,27,27)"
                : isAdded
                  ? "rgb(22,101,52)"
                  : "rgb(55,65,81)";
              const prefix = isRemoved ? (
                <span style={{ color: "rgb(220,38,38)", marginRight: 8 }}>-</span>
              ) : isAdded ? (
                <span style={{ color: "rgb(22,163,74)", marginRight: 8 }}>+</span>
              ) : null;
              return (
                <tr key={i}>
                  <td
                    style={{
                      padding: "0 12px",
                      textAlign: "right",
                      fontSize: 12,
                      color: "rgb(156,163,175)",
                      backgroundColor: "rgb(249,250,251)",
                      borderRight: "1px solid rgb(229,231,235)",
                      userSelect: "none",
                      fontFamily: "monospace",
                      minWidth: 50,
                    }}
                  >
                    {row.lineNumber ?? ""}
                  </td>
                  <td
                    style={{
                      padding: "0 12px",
                      fontSize: 13,
                      fontFamily: "monospace",
                      whiteSpace: "pre",
                      lineHeight: 1.5,
                      backgroundColor: bg,
                      color,
                    }}
                  >
                    {prefix}
                    {row.content}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// SectionCard component
// ============================================================================

function SectionCard({
  title,
  stats,
  children,
}: {
  title: string;
  stats: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--color-border)",
        borderRadius: 8,
        overflow: "hidden",
        backgroundColor: "var(--color-surface)",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          backgroundColor: "var(--color-background-secondary)",
          borderBottom: "1px solid var(--color-border)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: "1rem", fontWeight: 600, color: "var(--color-text-primary)" }}>
          {title}
        </div>
        <div style={{ fontSize: "0.8rem", color: "var(--color-text-secondary)" }}>{stats}</div>
      </div>
      <div style={{ padding: "1rem" }}>{children}</div>
    </div>
  );
}

// ============================================================================
// Plain comparison table
// ============================================================================

type ComparisonTableProps = {
  label: string;
  headers: string[];
  rows: string[][];
  emptyMessage?: string;
};

function ComparisonTable({ label, headers, rows, emptyMessage = "None" }: ComparisonTableProps) {
  return (
    <div>
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.85rem",
          color: "var(--color-text-secondary)",
          marginBottom: "0.5rem",
        }}
      >
        {label}
      </div>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "var(--font-size-sm)",
        }}
      >
        <thead>
          <tr>
            {headers.map(h => (
              <th
                key={h}
                style={{
                  textAlign: "left",
                  padding: "6px 10px",
                  backgroundColor: "var(--color-background-secondary)",
                  borderBottom: "1px solid var(--color-border)",
                  color: "var(--color-text-secondary)",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={headers.length}
                style={{
                  padding: "10px",
                  color: "var(--color-text-secondary)",
                  fontStyle: "italic",
                  fontSize: "0.85rem",
                }}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    style={{
                      padding: "6px 10px",
                      borderBottom: "1px solid var(--color-border)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    {cell || "-"}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Step 1 — Select Target
// ============================================================================

type SelectStepProps = {
  sourceCompany: Company;
  wizardState: WizardState;
  onWizardStateChange: (state: WizardState) => void;
};

function SelectStep({
  sourceCompany,
  wizardState,
  onWizardStateChange,
}: Omit<SelectStepProps, "onNext" | "onCancel">) {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<Company[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Load initial list on mount
  useEffect(() => {
    setIsSearching(true);
    companies
      .getAll({ limit: 25 })
      .then(res => setResults(res.data.filter(c => c.id !== sourceCompany.id)))
      .finally(() => setIsSearching(false));
  }, [sourceCompany.id]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await companies.getAll({ search: searchQuery || undefined, limit: 25 });
        setResults(res.data.filter(c => c.id !== sourceCompany.id));
      } finally {
        setIsSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, sourceCompany.id]);

  function handleSelectTarget(company: Company) {
    onWizardStateChange({ ...wizardState, targetCompany: company });
  }

  return (
    <div style={{ maxWidth: "60rem", margin: "0 auto" }}>
      {/* Breadcrumb */}
      <div
        style={{
          fontSize: "0.75rem",
          color: "var(--color-text-secondary)",
          marginBottom: "0.25rem",
        }}
      >
        Companies / {sourceCompany.name} / Merge
      </div>

      {/* Heading */}
      <h2
        style={{
          fontSize: "1.25rem",
          fontWeight: 600,
          margin: "0 0 0.375rem",
          color: "var(--color-text-primary)",
        }}
      >
        Select Target Company
      </h2>
      <p style={{ fontSize: "0.875rem", color: "var(--color-text-secondary)", margin: 0 }}>
        The source company will be merged into the target. The target company survives.
      </p>

      {/* Source card */}
      <div style={{ marginTop: "1.5rem" }}>
        <div
          style={{
            fontSize: "0.75rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "var(--color-text-secondary)",
            marginBottom: "0.5rem",
          }}
        >
          Source (being merged away)
        </div>
        <div
          style={{
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-md)",
            padding: "1rem",
            backgroundColor: "var(--color-surface)",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: "1rem",
              color: "var(--color-text-primary)",
              marginBottom: "0.25rem",
            }}
          >
            {sourceCompany.name}
          </div>
          <div
            style={{
              display: "flex",
              gap: "1.5rem",
              fontSize: "0.875rem",
              color: "var(--color-text-secondary)",
            }}
          >
            {sourceCompany.businessType && <span>Type: {sourceCompany.businessType}</span>}
          </div>
        </div>
      </div>

      {/* Search */}
      <div
        style={{ marginTop: "1.5rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}
      >
        <div style={{ fontSize: "0.875rem", fontWeight: 500, color: "var(--color-text-primary)" }}>
          Select target company
        </div>
        <input
          type="text"
          className={styles.formInput}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search companies..."
          autoComplete="off"
        />
      </div>

      {/* Results table */}
      <div
        style={{
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-lg)",
          backgroundColor: "var(--color-surface)",
          overflow: "hidden",
          marginTop: "0.75rem",
        }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-sm)" }}
        >
          <thead
            style={{
              backgroundColor: "var(--color-background-secondary)",
              borderBottom: "1px solid var(--color-border)",
            }}
          >
            <tr>
              <th
                style={{
                  padding: "var(--spacing-3) var(--spacing-4)",
                  textAlign: "left",
                  fontWeight: 600,
                  color: "var(--color-text-secondary)",
                  fontSize: "var(--font-size-xs)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Name
              </th>
              <th
                style={{
                  padding: "var(--spacing-3) var(--spacing-4)",
                  textAlign: "left",
                  fontWeight: 600,
                  color: "var(--color-text-secondary)",
                  fontSize: "var(--font-size-xs)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Business Type
              </th>
            </tr>
          </thead>
          <tbody>
            {results.map(company => {
              const isSelected = wizardState.targetCompany?.id === company.id;
              return (
                <tr
                  key={company.id}
                  onClick={() => handleSelectTarget(company)}
                  style={{
                    cursor: "pointer",
                    backgroundColor: isSelected ? "var(--color-primary-100, #dbeafe)" : undefined,
                    outline: isSelected ? "2px solid var(--color-primary-500, #3b82f6)" : undefined,
                    outlineOffset: isSelected ? "-2px" : undefined,
                    transition: "background-color var(--transition-fast)",
                  }}
                  onMouseEnter={e => {
                    if (!isSelected)
                      e.currentTarget.style.backgroundColor = "var(--color-surface-hover)";
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = "";
                  }}
                >
                  <td
                    style={{
                      padding: "var(--spacing-4)",
                      borderBottom: "1px solid var(--color-border)",
                      color: isSelected
                        ? "var(--color-primary-700, #1d4ed8)"
                        : "var(--color-text-primary)",
                      fontWeight: isSelected ? 600 : undefined,
                    }}
                  >
                    {isSelected && <span style={{ marginRight: "0.5rem" }}>✓</span>}
                    {company.name}
                  </td>
                  <td
                    style={{
                      padding: "var(--spacing-4)",
                      borderBottom: "1px solid var(--color-border)",
                      color: isSelected
                        ? "var(--color-primary-700, #1d4ed8)"
                        : "var(--color-text-primary)",
                    }}
                  >
                    {company.businessType ?? "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {isSearching && (
          <div
            style={{
              padding: "1rem",
              textAlign: "center",
              color: "var(--color-text-secondary)",
              fontSize: "0.875rem",
            }}
          >
            Searching...
          </div>
        )}
        {!isSearching && results.length === 0 && (
          <div
            style={{
              padding: "2rem",
              textAlign: "center",
              color: "var(--color-text-secondary)",
              fontSize: "0.875rem",
            }}
          >
            {searchQuery ? "No companies found" : "No companies available"}
          </div>
        )}
      </div>

      {wizardState.targetCompany && (
        <p
          style={{
            marginTop: "0.5rem",
            color: "var(--color-success, green)",
            fontSize: "0.875rem",
          }}
        >
          Selected: <strong>{wizardState.targetCompany.name}</strong>
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Step 2 — Review Companies
// ============================================================================

type ReviewStepProps = {
  sourceCompany: Company;
  targetCompany: Company;
  effectiveDate: string;
  onBack: () => void;
  onNext: (reviewData: ReviewData) => void;
};

function ReviewStep({ sourceCompany, targetCompany, onBack, onNext }: ReviewStepProps) {
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  const [loadingReview, setLoadingReview] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadReviewData() {
      setLoadingReview(true);
      try {
        const [
          sourceOfficesRes,
          targetOfficesRes,
          sourceContactsRes,
          targetContactsRes,
          sourceRefsRes,
          targetRefsRes,
        ] = await Promise.allSettled([
          offices.getAll({ companyId: sourceCompany.id, limit: 500 }),
          offices.getAll({ companyId: targetCompany.id, limit: 500 }),
          contacts.getAll({ companyId: sourceCompany.id, limit: 500 }),
          contacts.getAll({ companyId: targetCompany.id, limit: 500 }),
          externalReferences.getByEntity("company", sourceCompany.id),
          externalReferences.getByEntity("company", targetCompany.id),
        ]);

        if (!cancelled) {
          setReviewData({
            sourceOffices:
              sourceOfficesRes.status === "fulfilled" ? sourceOfficesRes.value.data : [],
            targetOffices:
              targetOfficesRes.status === "fulfilled" ? targetOfficesRes.value.data : [],
            sourceContacts:
              sourceContactsRes.status === "fulfilled" ? sourceContactsRes.value.data : [],
            targetContacts:
              targetContactsRes.status === "fulfilled" ? targetContactsRes.value.data : [],
            sourceRefs: sourceRefsRes.status === "fulfilled" ? sourceRefsRes.value.data : [],
            targetRefs: targetRefsRes.status === "fulfilled" ? targetRefsRes.value.data : [],
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load comparison data");
        }
      } finally {
        if (!cancelled) {
          setLoadingReview(false);
        }
      }
    }

    loadReviewData();
    return () => {
      cancelled = true;
    };
  }, [sourceCompany.id, sourceCompany.companyId, targetCompany.id, targetCompany.companyId]);

  if (loadingReview) {
    return <LoadingState message="Loading comparison data..." />;
  }

  if (!reviewData) {
    return (
      <p style={{ color: "var(--color-error)" }}>{error ?? "Failed to load comparison data"}</p>
    );
  }

  const { sourceRows, targetRows } = computeDiff(sourceCompany, targetCompany);

  const officeHeaders = ["Name", "City", "Country", "HQ"];
  const sourceOfficeRows = reviewData.sourceOffices.map(o => [
    o.name,
    o.city ?? "",
    o.countryName ?? "",
    o.isHeadOffice ? "Yes" : "",
  ]);
  const targetOfficeRows = reviewData.targetOffices.map(o => [
    o.name,
    o.city ?? "",
    o.countryName ?? "",
    o.isHeadOffice ? "Yes" : "",
  ]);

  const contactHeaders = ["Name", "Job Title", "Email"];
  const sourceContactRows = reviewData.sourceContacts.map(c => [
    c.name,
    c.jobTitle ?? "",
    c.email ?? "",
  ]);
  const targetContactRows = reviewData.targetContacts.map(c => [
    c.name,
    c.jobTitle ?? "",
    c.email ?? "",
  ]);

  const refHeaders = ["Type", "Value"];
  const sourceRefRows = reviewData.sourceRefs.map(r => [r.referenceType, r.referenceValue]);
  const targetRefRows = reviewData.targetRefs.map(r => [r.referenceType, r.referenceValue]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Action bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <Button variant="outlined" color="default" onClick={onBack}>
          Back
        </Button>
        <Button variant="contained" color="primary" onClick={() => onNext(reviewData)}>
          Next: Confirm
        </Button>
      </div>

      {/* Company fields diff */}
      <div>
        <h2
          style={{
            fontSize: "var(--font-size-lg)",
            fontWeight: 600,
            color: "var(--color-text-primary)",
            margin: "0 0 0.75rem 0",
          }}
        >
          Company Fields
        </h2>
        <div
          style={{
            display: "flex",
            backgroundColor: "rgb(249,250,251)",
            border: "1px solid rgb(229,231,235)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <DiffPanel title={`Source: ${sourceCompany.name}`} rows={sourceRows} borderRight />
          <DiffPanel title={`Target: ${targetCompany.name}`} rows={targetRows} />
        </div>
      </div>

      {/* Offices */}
      <SectionCard
        title="Offices"
        stats={`Source: ${reviewData.sourceOffices.length} · Target: ${reviewData.targetOffices.length} · After merge: ${reviewData.sourceOffices.length + reviewData.targetOffices.length}`}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <ComparisonTable
            label={`Source — ${sourceCompany.name}`}
            headers={officeHeaders}
            rows={sourceOfficeRows}
            emptyMessage="No offices"
          />
          <ComparisonTable
            label={`Target — ${targetCompany.name}`}
            headers={officeHeaders}
            rows={targetOfficeRows}
            emptyMessage="No offices"
          />
        </div>
      </SectionCard>

      {/* Contacts */}
      <SectionCard
        title="Contacts"
        stats={`Source: ${reviewData.sourceContacts.length} · Target: ${reviewData.targetContacts.length} · After merge: ${reviewData.sourceContacts.length + reviewData.targetContacts.length}`}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <ComparisonTable
            label={`Source — ${sourceCompany.name}`}
            headers={contactHeaders}
            rows={sourceContactRows}
            emptyMessage="No contacts"
          />
          <ComparisonTable
            label={`Target — ${targetCompany.name}`}
            headers={contactHeaders}
            rows={targetContactRows}
            emptyMessage="No contacts"
          />
        </div>
      </SectionCard>

      {/* External References */}
      <SectionCard
        title="External References"
        stats={`Source: ${reviewData.sourceRefs.length} · Target: ${reviewData.targetRefs.length} · After merge: ${reviewData.sourceRefs.length + reviewData.targetRefs.length}`}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <ComparisonTable
            label={`Source — ${sourceCompany.name}`}
            headers={refHeaders}
            rows={sourceRefRows}
            emptyMessage="No external references"
          />
          <ComparisonTable
            label={`Target — ${targetCompany.name}`}
            headers={refHeaders}
            rows={targetRefRows}
            emptyMessage="No external references"
          />
        </div>
      </SectionCard>
    </div>
  );
}

// ============================================================================
// Step 3 — Confirm Merge
// ============================================================================

type ConfirmStepProps = {
  sourceCompany: Company;
  targetCompany: Company;
  effectiveDate: string;
  reviewData: ReviewData;
  onBack: () => void;
  onDone: (officesTransferred: number) => void;
  onCancel: () => void;
};

function ConfirmStep({
  sourceCompany,
  targetCompany,
  effectiveDate,
  reviewData,
  onBack,
  onDone,
  onCancel,
}: ConfirmStepProps) {
  const [refsDisposition, setRefsDisposition] = useState<"migrate" | "drop">("migrate");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleMerge() {
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await companies.merge(sourceCompany.id, {
        targetCompanyId: targetCompany.id,
        effectiveDate: `${effectiveDate}T00:00:00.000Z`,
      });
      onDone(res.result.officesTransferred);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Merge failed");
    } finally {
      setIsSubmitting(false);
    }
  }

  const radioLabelStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    fontSize: "0.9rem",
    color: "var(--color-text-primary)",
  };

  const disabledLabelStyle: React.CSSProperties = {
    ...radioLabelStyle,
    color: "var(--color-text-secondary)",
    cursor: "not-allowed",
  };

  const sectionStyle: React.CSSProperties = {
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    padding: "1rem 1.25rem",
    backgroundColor: "var(--color-surface)",
  };

  const legendStyle: React.CSSProperties = {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "var(--color-text-primary)",
    marginBottom: "0.75rem",
    padding: 0,
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <h2
        style={{
          fontSize: "var(--font-size-lg)",
          fontWeight: 600,
          color: "var(--color-text-primary)",
          margin: "0 0 0.5rem 0",
        }}
      >
        Confirm Merge
      </h2>
      <p style={{ color: "var(--color-text-secondary)", marginBottom: "1.5rem" }}>
        Merging <strong>{sourceCompany.name}</strong> into <strong>{targetCompany.name}</strong>.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {/* Offices */}
        <fieldset style={{ ...sectionStyle, border: "1px solid var(--color-border)" }}>
          <legend style={legendStyle}>Offices ({reviewData.sourceOffices.length})</legend>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <label style={radioLabelStyle}>
              <input type="radio" name="offices-disposition" checked readOnly />
              Transfer all offices to {targetCompany.name}
            </label>
            <label style={disabledLabelStyle}>
              <input type="radio" name="offices-disposition" disabled />
              Review individually (coming soon)
            </label>
          </div>
        </fieldset>

        {/* Contact Roles */}
        <fieldset style={{ ...sectionStyle, border: "1px solid var(--color-border)" }}>
          <legend style={legendStyle}>Contact Roles ({reviewData.sourceContacts.length})</legend>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <label style={radioLabelStyle}>
              <input type="radio" name="contacts-disposition" checked readOnly />
              Reassign all roles to {targetCompany.name}
            </label>
            <label style={disabledLabelStyle}>
              <input type="radio" name="contacts-disposition" disabled />
              Review individually (coming soon)
            </label>
          </div>
        </fieldset>

        {/* External References */}
        <fieldset style={{ ...sectionStyle, border: "1px solid var(--color-border)" }}>
          <legend style={legendStyle}>External References ({reviewData.sourceRefs.length})</legend>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <label style={radioLabelStyle}>
              <input
                type="radio"
                name="refs-disposition"
                value="migrate"
                checked={refsDisposition === "migrate"}
                onChange={() => setRefsDisposition("migrate")}
              />
              Migrate all references to {targetCompany.name}
            </label>
            <label style={radioLabelStyle}>
              <input
                type="radio"
                name="refs-disposition"
                value="drop"
                checked={refsDisposition === "drop"}
                onChange={() => setRefsDisposition("drop")}
              />
              Drop source references
            </label>
          </div>
        </fieldset>
      </div>

      {/* Warning */}
      <div
        style={{
          background: "var(--color-warning-background, #fff8e1)",
          border: "1px solid var(--color-warning-border, #ffc107)",
          borderRadius: 6,
          padding: "12px 16px",
          fontSize: "0.875rem",
          color: "rgb(120, 60, 0)",
          marginTop: "1.5rem",
        }}
      >
        <strong>Warning:</strong> This action creates an entity relationship record and cannot be
        easily undone. <strong>{sourceCompany.name}</strong> will be archived after the merge.
      </div>

      {/* Error */}
      {error && (
        <p
          style={{
            color: "var(--color-error)",
            fontSize: "0.875rem",
            marginTop: "0.75rem",
          }}
        >
          {error}
        </p>
      )}

      {/* Footer action bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "2rem",
          paddingTop: "1rem",
          borderTop: "1px solid var(--color-border)",
        }}
      >
        <Button variant="outlined" color="default" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <div style={{ display: "flex", gap: 12 }}>
          <Button variant="outlined" color="default" onClick={onBack} disabled={isSubmitting}>
            Back
          </Button>
          <Button variant="contained" color="primary" onClick={handleMerge} disabled={isSubmitting}>
            {isSubmitting ? "Merging..." : "Merge Companies"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step 4 — Done
// ============================================================================

type DoneStepProps = {
  sourceCompany: Company;
  targetCompany: Company;
  officesTransferred: number;
};

function DoneStep({ sourceCompany, targetCompany, officesTransferred }: DoneStepProps) {
  const navigate = useNavigate();

  return (
    <div style={{ textAlign: "center", padding: "3rem 1rem" }}>
      <p
        style={{
          fontSize: "1.5rem",
          fontWeight: 700,
          color: "var(--color-text-primary)",
          marginBottom: "0.75rem",
        }}
      >
        Merge complete!
      </p>
      <p style={{ color: "var(--color-text-secondary)", marginBottom: "0.5rem" }}>
        <strong>{sourceCompany.name}</strong> has been merged into{" "}
        <strong>{targetCompany.name}</strong>.
      </p>
      <p style={{ color: "var(--color-text-secondary)", marginBottom: "2rem" }}>
        {officesTransferred} office{officesTransferred !== 1 ? "s" : ""} transferred.
      </p>
      <div style={{ display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          color="default"
          onClick={() => navigate(ROUTES.addressBook.companyDetail(targetCompany.id))}
        >
          View {targetCompany.name}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(ROUTES.addressBook.companies)}
        >
          Back to Companies
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// MergeWizardPage — main page component
// ============================================================================

export default function MergeWizardPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [step, setStep] = useState<Step>("select");
  const [sourceCompany, setSourceCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [wizardState, setWizardState] = useState<WizardState>({
    targetCompany: null,
    effectiveDate: new Date().toISOString().split("T")[0],
  });
  const [officesTransferred, setOfficesTransferred] = useState(0);
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    companies
      .getById(id)
      .then(res => setSourceCompany(res.data))
      .catch(err => setLoadError(err instanceof Error ? err.message : "Failed to load company"))
      .finally(() => setLoading(false));
  }, [id]);

  const _pageTitle =
    step === "select"
      ? "Merge Company"
      : step === "review"
        ? "Review Comparison"
        : step === "confirm"
          ? "Confirm Merge"
          : "Merge Complete";

  const _pageSubtitle =
    step === "select"
      ? "Select the company to merge into"
      : step === "review"
        ? `Merging ${sourceCompany?.name ?? ""} into ${wizardState.targetCompany?.name ?? ""}. Review the detail below.`
        : step === "confirm"
          ? `Merging ${sourceCompany?.name ?? ""} into ${wizardState.targetCompany?.name ?? ""}`
          : "The merge has been completed successfully.";

  if (loading) {
    return (
      <div className={styles.detailWrapper}>
        <PageHeader title="Merge Company" subtitle="Loading..." />
        <LoadingState message="Loading company..." />
      </div>
    );
  }

  if (loadError || !sourceCompany) {
    return (
      <div className={styles.detailWrapper}>
        <PageHeader title="Merge Company" subtitle="An error occurred" />
        <p style={{ color: "var(--color-error)" }}>{loadError ?? "Company not found"}</p>
        <div style={{ marginTop: "1rem" }}>
          <Button
            variant="outlined"
            color="default"
            onClick={() => navigate(ROUTES.addressBook.companies)}
          >
            Back to Companies
          </Button>
        </div>
      </div>
    );
  }

  const emptyReviewData: ReviewData = {
    sourceOffices: [],
    targetOffices: [],
    sourceContacts: [],
    targetContacts: [],
    sourceRefs: [],
    targetRefs: [],
  };

  // Only the select step uses the shared footer — review/confirm/done manage their own
  const showSharedFooter = step === "select";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "var(--color-background)",
      }}
    >
      {/* Stepper header */}
      <div
        style={{
          backgroundColor: "var(--color-surface)",
          borderBottom: "1px solid var(--color-border)",
          padding: "var(--spacing-6) var(--spacing-8)",
        }}
      >
        <WizardStepper steps={buildStepperSteps(step)} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: "var(--spacing-8)", overflowY: "auto" }}>
        {step === "select" && (
          <SelectStep
            sourceCompany={sourceCompany}
            wizardState={wizardState}
            onWizardStateChange={setWizardState}
          />
        )}

        {step === "review" && wizardState.targetCompany && (
          <ReviewStep
            sourceCompany={sourceCompany}
            targetCompany={wizardState.targetCompany}
            effectiveDate={wizardState.effectiveDate}
            onBack={() => setStep("select")}
            onNext={data => {
              setReviewData(data);
              setStep("confirm");
            }}
          />
        )}

        {step === "confirm" && wizardState.targetCompany && (
          <ConfirmStep
            sourceCompany={sourceCompany}
            targetCompany={wizardState.targetCompany}
            effectiveDate={wizardState.effectiveDate}
            reviewData={reviewData ?? emptyReviewData}
            onBack={() => setStep("review")}
            onDone={transferred => {
              setOfficesTransferred(transferred);
              setStep("done");
            }}
            onCancel={() => navigate(ROUTES.addressBook.companyDetail(sourceCompany.id))}
          />
        )}

        {step === "done" && wizardState.targetCompany && (
          <DoneStep
            sourceCompany={sourceCompany}
            targetCompany={wizardState.targetCompany}
            officesTransferred={officesTransferred}
          />
        )}
      </div>

      {/* Shared footer for select/review steps */}
      {showSharedFooter && (
        <div
          style={{
            backgroundColor: "var(--color-surface)",
            borderTop: "1px solid var(--color-border)",
            padding: "var(--spacing-4) var(--spacing-8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            color="default"
            onClick={() => navigate(ROUTES.addressBook.companyDetail(sourceCompany.id))}
          >
            Cancel
          </Button>
          <div style={{ display: "flex", gap: "var(--spacing-3)", alignItems: "center" }}>
            <Button variant="outlined" color="default" disabled>
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setStep("review")}
              disabled={!wizardState.targetCompany}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
